import {
  IBlock,
  ICustomerBlock,
  ICustomer,
  IPurchaseReceiver,
  IProductDescriptionResponse
} from '../../store/ducks/customers/types';
import { IUser } from '../../store/ducks/user/types';
import {
  customersAuthorizationApi,
  ECustomerAuthorizationEndpoints
} from '../api';
import { EGerdauService } from '../EGerdauService';
import { ELocalStorageKey, ILocalStorageObj } from '../EGerdauService/types';

export class CustomersService extends EGerdauService {
  localStorageKey = ELocalStorageKey.SELECTED_CUSTOMER;

  async getCustomers(user: IUser): Promise<ICustomer[]> {
    const headers = super.getHeaders();
    const { isManager } = user;

    let customers: ICustomer[] = [];

    if (!isManager) {
      const response = await customersAuthorizationApi.get(
        ECustomerAuthorizationEndpoints.CUSTOMERS,
        { headers }
      );
      customers = response.data.customers;
      this.sortCustomersByCustomerName(customers);
      this.removeCustomerDuplication(customers);
      customers = customers.map((c) => ({
        ...c,
        division: c.division.map((item) => Number(item).toString())
      }));
    }

    return customers;
  }

  async getCustomerById(customerId: string): Promise<ICustomer> {
    const headers = new EGerdauService().getHeaders();
    const response = await customersAuthorizationApi.get(
      `${
        ECustomerAuthorizationEndpoints.CUSTOMERS
      }?customer=${customerId?.padStart(10, '0')}`,
      { headers }
    );

    if (
      !response.data ||
      !response.data.customers ||
      !(response.data.customers as ICustomer[]).find(
        (cust) => cust.customerId === customerId
      )
    ) {
      throw new Error('BP não encontrado');
    }

    const customer = (response.data.customers as ICustomer[]).find(
      (cust) => cust.customerId === customerId
    ) as ICustomer;
    customer.lastUpdateTime = new Date().getTime();
    return customer;
  }

  private sortCustomersByCustomerName(customers: ICustomer[]) {
    customers.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      } else if (a.name > b.name) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  private removeCustomerDuplication(customers: ICustomer[]) {
    const noDuplicationCustomers: ICustomer[] = [];

    customers.forEach((customer) => {
      if (
        !noDuplicationCustomers.find(
          (ndCustomer) => ndCustomer.customerId === customer.customerId
        )
      ) {
        noDuplicationCustomers.push(customer);
      }
    });

    customers = noDuplicationCustomers;
  }

  async getProductDescriptions(
    customerId: string
  ): Promise<IProductDescriptionResponse> {
    const headers = super.getHeaders();
    headers['x-api-version'] = 'v2';

    const response = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.PRODUCT_DESCRIPTION}?customer=${customerId}`,
      { headers }
    );

    const productDescriptionResponse = response.data;

    return productDescriptionResponse as IProductDescriptionResponse;
  }

  async getProductDescriptionsV2(
    customerId: string
  ): Promise<IProductDescriptionResponse> {
    const headers = super.getHeaders();
    headers['x-api-version'] = 'v2';

    const response = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.PRODUCT_DESCRIPTION_V2}?customer=${customerId}`,
      { headers }
    );

    const productDescriptionResponse = response.data;

    return productDescriptionResponse as IProductDescriptionResponse;
  }

  async getCustomerBlocks(customerId: string): Promise<IBlock[]> {
    const headers = super.getHeaders();
    const customerBlocksResponse = await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.BLOCKS,
      { customers: [customerId] },
      { headers }
    );

    const blocks = customerBlocksResponse.data.blocks[0] as ICustomerBlock;

    const selectedCustomerBlocks: IBlock[] = [];

    if (blocks.blocks) {
      blocks.blocks.forEach((block) => selectedCustomerBlocks.push(block));
    } else if (blocks.subsidiaries && blocks.subsidiaries.length > 0) {
      blocks.subsidiaries.forEach((subs) => {
        if (subs.code === customerId && subs.blocks?.length) {
          subs.blocks.forEach((block) => selectedCustomerBlocks.push(block));
        }
      });
    }

    return selectedCustomerBlocks;
  }

  async getCustomerPurchaseReceivers(
    customerId: string
  ): Promise<IPurchaseReceiver[]> {
    const headers = super.getHeaders();
    const purchaseReceiversResponse = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.PURCHASE_RECEIVERS}?customer=${customerId}`,
      { headers }
    );

    return purchaseReceiversResponse.data
      .shipToCustomers as IPurchaseReceiver[];
  }

  canReturnLocalStorageData(
    localStorageData: ILocalStorageObj,
    customerId: string
  ): boolean {
    const now = new Date();

    return (
      customerId === localStorageData.customerId &&
      now.getTime() - localStorageData.dateTime <= 3600000
    ); // 1h
  }
}
